import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";

/**
 * SiteSettingsPanel — single-doc CRUD for shipping / hero / season / contact.
 * Calls PUT /api/admin/cms/settings on save.
 */
const FIELD_GROUPS = [
  {
    label: "Commerce",
    fields: [
      { key: "shipping_fee", label: "Shipping fee (GBP)", type: "number", step: "0.01" },
      { key: "free_shipping_threshold", label: "Free shipping over (GBP)", type: "number", step: "1" },
      { key: "currency", label: "Currency code", type: "text" },
    ],
  },
  {
    label: "Season scarcity",
    fields: [
      { key: "current_season", label: "Current season tag (e.g. FW26)", type: "text" },
      { key: "current_season_tagline", label: "Season tagline copy", type: "text" },
    ],
  },
  {
    label: "Hero copy",
    fields: [
      { key: "hero_title", label: "Hero title", type: "text" },
      { key: "hero_subtitle", label: "Hero subtitle", type: "textarea" },
      { key: "hero_cta_primary", label: "Primary CTA label", type: "text" },
      { key: "hero_cta_secondary", label: "Secondary CTA label", type: "text" },
    ],
  },
  {
    label: "Brand · Contact",
    fields: [
      { key: "contact_email", label: "Public contact email", type: "email" },
      { key: "social_instagram", label: "Instagram URL", type: "url" },
      { key: "social_tiktok", label: "TikTok URL", type: "url" },
    ],
  },
];

const SiteSettingsPanel = ({ authClient }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authClient()
      .get("/admin/cms/settings")
      .then((r) => setSettings(r.data))
      .catch(() => toast.error("Couldn't load settings"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!settings) return <p className="text-mute">Loading settings…</p>;

  const onChange = (key, val) => setSettings({ ...settings, [key]: val });

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...settings,
        shipping_fee: Number(settings.shipping_fee),
        free_shipping_threshold: Number(settings.free_shipping_threshold),
        size_guide: (settings.size_guide || []).map((r) => ({
          pritch: r.pritch || "",
          uk: r.uk || "",
          eu: r.eu || "",
          us: r.us || "",
        })),
      };
      const { data } = await authClient().put("/admin/cms/settings", payload);
      setSettings(data);
      toast.success("Settings saved — live on the storefront");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onAddSizeRow = () =>
    setSettings({
      ...settings,
      size_guide: [...(settings.size_guide || []), { pritch: "", uk: "", eu: "", us: "" }],
    });

  const onChangeSizeRow = (idx, key, val) =>
    setSettings({
      ...settings,
      size_guide: (settings.size_guide || []).map((r, i) =>
        i === idx ? { ...r, [key]: val } : r
      ),
    });

  const onRemoveSizeRow = (idx) =>
    setSettings({
      ...settings,
      size_guide: (settings.size_guide || []).filter((_, i) => i !== idx),
    });

  return (
    <form
      onSubmit={onSave}
      className="bg-white border border-line p-6 space-y-8"
      data-testid="site-settings-panel"
    >
      {FIELD_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="overline mb-4 border-b border-line pb-2">{group.label}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.fields.map((f) => (
              <label
                key={f.key}
                className={`block ${f.type === "textarea" ? "md:col-span-2" : ""}`}
              >
                <span className="text-[11px] tracking-[0.16em] uppercase font-bold block mb-1.5">
                  {f.label}
                </span>
                {f.type === "textarea" ? (
                  <textarea
                    value={settings[f.key] ?? ""}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    rows={3}
                    className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink"
                    data-testid={`settings-${f.key}`}
                  />
                ) : (
                  <input
                    type={f.type}
                    step={f.step}
                    value={settings[f.key] ?? ""}
                    onChange={(e) => onChange(f.key, e.target.value)}
                    className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink"
                    data-testid={`settings-${f.key}`}
                  />
                )}
              </label>
            ))}
          </div>
        </div>
      ))}

      <div>
        <p className="overline mb-4 border-b border-line pb-2 flex items-center justify-between">
          <span>Size guide rows</span>
          <button
            type="button"
            onClick={onAddSizeRow}
            className="text-[11px] tracking-[0.16em] uppercase font-bold inline-flex items-center gap-1.5 hover:text-ink"
            data-testid="size-guide-add-row"
          >
            <Plus size={12} /> Add row
          </button>
        </p>
        <p className="text-xs text-mute mb-3">
          These rows render in the product page size guide dialog. Keep them ordered small → large.
        </p>
        <div className="border border-line">
          <table className="w-full text-xs">
            <thead className="border-b border-line bg-chalk">
              <tr>
                <th className="px-3 py-2 text-left overline">Pritch</th>
                <th className="px-3 py-2 text-left overline">UK</th>
                <th className="px-3 py-2 text-left overline">EU</th>
                <th className="px-3 py-2 text-left overline">US</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {(settings.size_guide || []).map((row, i) => (
                <tr
                  key={`sg-${i}-${row.pritch || "blank"}`}
                  className="border-b border-line"
                  data-testid={`size-guide-row-${i}`}
                >
                  {["pritch", "uk", "eu", "us"].map((k) => (
                    <td key={k} className="px-2 py-1.5">
                      <input
                        value={row[k] || ""}
                        onChange={(e) => onChangeSizeRow(i, k, e.target.value)}
                        className="w-full border border-line bg-chalk px-2 py-1"
                        data-testid={`size-guide-${k}-${i}`}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5 text-right">
                    <button
                      type="button"
                      onClick={() => onRemoveSizeRow(i)}
                      className="text-mute hover:text-ink p-1"
                      data-testid={`size-guide-remove-${i}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
              {(!settings.size_guide || settings.size_guide.length === 0) && (
                <tr>
                  <td colSpan="5" className="px-3 py-4 text-mute text-center">
                    No rows. Add one to populate the size guide dialog.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <label className="block mt-4">
          <span className="text-[11px] tracking-[0.16em] uppercase font-bold block mb-1.5">
            Size guide note (fit tip)
          </span>
          <input
            value={settings.size_guide_note || ""}
            onChange={(e) => setSettings({ ...settings, size_guide_note: e.target.value })}
            className="w-full border border-line px-3 py-2 bg-chalk focus:outline-none focus:border-ink"
            data-testid="settings-size_guide_note"
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={saving}
        className="btn-primary disabled:opacity-50"
        data-testid="settings-save"
      >
        <Save size={14} className="mr-2" />
        {saving ? "Saving…" : "Save site settings"}
      </button>
    </form>
  );
};

export default SiteSettingsPanel;
